import { type ComboboxItem } from "@mantine/core";
import uniqBy from "lodash/uniqBy";

import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { getMaterialName } from "#modules/quoteRequestLines/domain/getMaterialSelectOptions";
import {
  doesMaterialFitPartDimensions,
  formatMaterialDimensions,
  FormatterMaterialParameter,
  getMaterialTypesFromPartType,
  MaterialGradeType,
  PartTypeSchema,
  PartTypeType,
  sortMaterialDimensions,
} from "shared";

const materialToOptions = (materials: Material[], partType: PartTypeType | null) => {
  const materialOptions = sortMaterialDimensions<Material>(materials, partType).map((cm) => ({
    label: `${formatMaterialDimensions(partType, cm)}${cm.subType ? ` - ${cm.subType}` : ""}`,
    value: cm.id,
  }));

  return uniqBy(materialOptions, "label");
};

export const getMaterialDimensionsSelectOptions = ({
  customMaterials,
  materialGrade,
  materialName,
  partType,
  partSubType,
  partDimensionsWithOverthickness,
}: {
  customMaterials: Material[];
  materialGrade: MaterialGradeType;
  materialName: string;
  partType: PartTypeType | null;
  partSubType: string | null;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
}): { recommendedOptions: ComboboxItem[]; otherOptions: ComboboxItem[] } => {
  const filteredMaterials = customMaterials
    .filter((cm) => {
      if (materialName && materialName.length > 1) {
        return getMaterialName(cm) === materialName;
      }

      return cm.materialGrade === materialGrade;
    })
    .filter((cm) => {
      if (!partType) {
        return true;
      }
      const isPartTypeCorrect = getMaterialTypesFromPartType(partType).includes(cm.type);

      if (!isPartTypeCorrect) {
        return false;
      }

      if (partType === PartTypeSchema.enum.PROFILE && partSubType) {
        return cm.subType === partSubType;
      }
      return true;
    });

  const recommendedMaterials = filteredMaterials.filter((cm) =>
    doesMaterialFitPartDimensions({
      partType,
      partDimensions: partDimensionsWithOverthickness,
      materialDimensions: cm,
    }),
  );
  const otherMaterials = filteredMaterials.filter(
    (cm) => !recommendedMaterials.some((recommended) => recommended.id === cm.id),
  );

  const recommendedOptions = materialToOptions(recommendedMaterials, partType);
  const recommendedLabels = new Set(recommendedOptions.map((option) => option.label));

  return {
    recommendedOptions,
    otherOptions: materialToOptions(otherMaterials, partType).filter(
      (option) => !recommendedLabels.has(option.label),
    ),
  };
};
